import { API } from '@unboared/react';

/**
 * Cadence de la réconciliation périodique qui rattrape une synchro tardive de la
 * liste des manettes après un reload écran (gotchas #103 / #111).
 *
 * Source unique de vérité : changer la cadence ici la change pour le compteur live
 * ET les deux collecteurs de stats.
 */
export const LIVE_PLAYERS_RECONCILE_MS = 4000;

export type LivePlayersHandlers = {
    /**
     * Exécuté une fois, synchroniquement, au démarrage : seed depuis
     * `API.getGamepadIDs()` (joueurs déjà présents à un game switch / reload).
     * Inutile avec `onReconcileNewDevice` (le sweep initial en tient lieu).
     */
    onSeed?: () => void;
    onConnect?: (deviceID: string) => void;
    onDisconnect?: (deviceID: string) => void;
    /** Reconnexion dans la grace period → onDeviceBack, pas onConnect (gotcha #2). */
    onDeviceBack?: (deviceID: string) => void;
    /**
     * Exécuté toutes les `LIVE_PLAYERS_RECONCILE_MS` : relire `API.getGamepadIDs()`
     * comme source de vérité et converger (n'écrire qu'au changement côté appelant).
     * Pour les consommateurs count-based (compteur de joueurs, maxPlayers).
     */
    onReconcile?: () => void;
    /**
     * Variante per-device du reconcile, pour les collecteurs qui ouvrent un suivi par
     * device (sessions gamepad, visites) : appelé — au démarrage puis toutes les
     * `LIVE_PLAYERS_RECONCILE_MS` — pour chaque device présent dans `getGamepadIDs()`
     * qui n'a JAMAIS été livré (ni par onConnect/onDeviceBack, ni par un sweep
     * précédent). C'est le backstop du seed : juste après un reload écran la liste
     * est encore vide et les gamepads déjà connectés ne rejouent pas onConnect
     * (gotchas #103/#111) — sans lui, toute une session n'enregistre rien.
     *
     * Sémantique « ever-seen » (jamais dé-marqué) plutôt que tombstone-à-la-finalize :
     * un deviceID est monotone par session (jamais réassigné), donc un device déjà
     * livré ne doit JAMAIS revenir par le sweep — les retours légitimes passent par
     * onConnect/onDeviceBack. Ça couvre aussi le cas où une entité suivie change de
     * deviceID (ex. visite uid-keyée migrant vers un nouveau device après un rescan
     * du QR) : l'ancien deviceID, resté en grace dans getGamepadIDs(), a déjà été vu
     * et n'est pas re-livré — pas de résurrection fantôme, sans tombstone côté appelant.
     */
    onReconcileNewDevice?: (deviceID: string) => void;
};

/**
 * Câble le cycle de vie « présence manettes live » partagé par le compteur de
 * joueurs simultanés (`useScreenPlayerCount`) et les collecteurs de stats Firestore
 * (`FirestoreScreenDataCollector`, `FirestoreGamepadDataCollector`) :
 *
 *   seed → subscribe(onConnect/onDisconnect/onDeviceBack) → réconciliation périodique
 *
 * Centralise la cadence (`LIVE_PLAYERS_RECONCILE_MS`) et le set d'events pour qu'un
 * changement se fasse à un seul endroit (gotchas #103 / #111).
 *
 * Le seed est appelé APRÈS la souscription : sans importance car JS est mono-thread,
 * aucun event ne peut se déclencher entre deux instructions synchrones — il n'y a donc
 * pas de fenêtre d'event manqué entre subscribe et seed.
 *
 * Retourne un teardown qui arrête l'intervalle de réconciliation et désabonne tous les
 * listeners. NB : un éventuel timer de debounce / heartbeat propre à l'appelant reste à
 * sa charge — cette primitive ne gère que seed/subscribe/reconcile.
 */
export function trackLivePlayers(handlers: LivePlayersHandlers): () => void {
    // Ever-seen deviceIDs : tout device livré à l'appelant, par event ou par sweep.
    // Le sweep per-device ne livre que les jamais-vus (cf doc de onReconcileNewDevice).
    const seen = new Set<string>();
    const remember = (cb?: (deviceID: string) => void) => (deviceID: string) => {
        seen.add(deviceID);
        cb?.(deviceID);
    };
    const onNewDevice = handlers.onReconcileNewDevice;

    const unsubs: Array<(() => void) | undefined> = [];
    // Avec un sweep per-device, on souscrit onConnect/onDeviceBack même sans handler
    // appelant : il faut marquer « vu » chaque device livré par event, sinon le sweep
    // suivant le re-livrerait.
    if (handlers.onConnect || onNewDevice) unsubs.push(API.onConnect(remember(handlers.onConnect)));
    if (handlers.onDeviceBack || onNewDevice) unsubs.push(API.onDeviceBack(remember(handlers.onDeviceBack)));
    if (handlers.onDisconnect) unsubs.push(API.onDisconnect(handlers.onDisconnect));

    handlers.onSeed?.();

    const sweepNewDevices = onNewDevice
        ? () => {
              API.getGamepadIDs().forEach((deviceID) => {
                  if (seen.has(deviceID)) return;
                  seen.add(deviceID);
                  onNewDevice(deviceID);
              });
          }
        : undefined;

    // Sweep initial synchrone : tient lieu de seed pour les consommateurs per-device
    // (devices déjà présents à un game switch / reload).
    sweepNewDevices?.();

    const reconcile = setInterval(() => {
        handlers.onReconcile?.();
        sweepNewDevices?.();
    }, LIVE_PLAYERS_RECONCILE_MS);

    return () => {
        clearInterval(reconcile);
        unsubs.forEach((unsub) => unsub && unsub());
    };
}
